import React, {
	MouseEventHandler,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import clsx from "clsx";

import CollapseIcon from "static/images/collapse-arrow.png";
import OpenedCollapseIcon from "static/images/collapse-white-arrow.png";
import styles from "./collapse.module.scss";

type CollapseProps = {
	className?: string;
	header: ({
		toggle,
		isToggled,
		isDisabled,
	}: {
		toggle: () => void;
		isToggled: boolean;
		isDisabled?: boolean;
	}) => JSX.Element;
	body: (isToggled?: boolean) => JSX.Element;
	setOpenSectionFn?: (fn: () => void) => void;
	bodyClassName?: string;
	defaultToggled?: boolean;
	isDisabled?: boolean;
	isArrowDisabled?: boolean;
	[key: string]: any;
};

type ToggleProps = {
	onClick: MouseEventHandler<HTMLButtonElement>;
	isToggled: boolean;
	className?: string;
	buttonType?: "button" | "submit" | "reset" | undefined;
	[key: string]: any;
};

export const Collapse = ({
	className = "",
	header,
	body,
	setOpenSectionFn,
	bodyClassName = "",
	defaultToggled = false,
	isDisabled = false,
	isArrowDisabled = false,
	...props
}: CollapseProps) => {
	const [isToggled, setIsToggled] = useState(defaultToggled);
	const bodyRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (bodyRef.current) {
			bodyRef.current.style.maxHeight = isToggled ? `none` : "0px";
		}
	}, [bodyRef, isToggled]);

	useEffect(() => {
		setOpenSectionFn?.(() => () => {
			if (!isDisabled) setIsToggled(true);
		});
	}, [isDisabled, setOpenSectionFn]);

	return (
		<div
			className={clsx(
				isArrowDisabled && styles.isArrowDisabled,
				className,
			)}
			{...props}
		>
			{header({
				isToggled,
				isDisabled,
				toggle: () =>
					isDisabled ? setIsToggled(false) : setIsToggled(!isToggled),
			})}
			<div
				ref={bodyRef}
				className={clsx(
					styles.body,
					isToggled && styles.isToggled,
					bodyClassName,
				)}
			>
				{body(isToggled)}
			</div>
		</div>
	);
};

export const Toggle = ({
	onClick,
	isToggled,
	className = "",
	buttonType = "button",
	...props
}: ToggleProps) => {
	return (
		<button
			className={clsx(
				styles.toggle,
				isToggled && styles.toggled,
				className,
			)}
			onClick={onClick}
			{...props}
			type={buttonType}
		>
			{props?.children}
			<img
				src={isToggled ? OpenedCollapseIcon : CollapseIcon}
				alt=""
				className={styles.toggleImg}
			/>
		</button>
	);
};
